import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import { Box, Heading, HiddenVisually, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { v4 as uuid } from 'uuid';

import {
  ErrorBoundaryContent,
  useAppContext,
  useConfig,
  type Jobs,
} from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import { useAuth } from '../../../shared/providers/authProvider';

import { useHubble } from '@seek/hubble';
import { useSearchParams } from 'react-router';
import { trackViewEvents } from '../../../shared/tracking/candidateApp/trackViewEvents';
import { sortSalaryRangeByRoles } from '../../../shared/utils/sortSalaryRangeByRoles';
import { CompaniesList } from '../../components/companiesList/CompaniesList';
import { CompanyReviewAISummarySection } from '../../components/salaries/companyReviewAISummarySection/CompanyReviewAISummarySection';
import { OpenJobsSection } from '../../components/salaries/openJobsSection/OpenJobsSection';
import { SalariesEmptyState } from '../../components/salaries/salariesEmptyState/SalariesEmptyState';
import { SalariesListingSection } from '../../components/salaries/salariesListingSection/SalariesListingSection';
import { SalarySatisfactionSection } from '../../components/salaries/salarySatisfactionSection/SalarySatisfactionSection';
import translations from './.vocab';
import type { SalariesTabModel } from './salariesTabModel';

interface SalariesPageProps {
  getPaginatedJobs: (organisationId: string, page?: number) => Promise<Jobs>;
  model: SalariesTabModel;
  onActiveTabChanged: (item: string | undefined) => void;
  trackJobsPaginationClicked: ({
    searchTotalCount,
    pageNumber,
  }: {
    pageNumber: number;
    searchTotalCount?: number;
  }) => void;
  trackRandomCompanyProfileLinkClicked: ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => void;
  trackReviewsClicked: () => void;
}

export const SalariesPage = ({
  model,
  getPaginatedJobs,
  trackJobsPaginationClicked,
  trackRandomCompanyProfileLinkClicked,
  onActiveTabChanged,
  trackReviewsClicked,
}: SalariesPageProps) => {
  const { t } = useTranslations(translations);
  const config = useConfig();
  const { companyId, ...context } = useAppContext();
  const { authenticationStatus } = useAuth();
  const hubble = useHubble();
  const [params] = useSearchParams();
  const serviceToken = params.get('serviceToken') ?? undefined;

  const { companyName, salaryRanges } = model;

  useEffect(() => {
    hubble.updateOptions({ pageName: 'company profile' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      companyId &&
      companyName &&
      authenticationStatus !== AUTHENTICATION_TYPES.AUTH_PENDING
    ) {
      const trackingCorrelationId = uuid();
      trackViewEvents.salariesPage({
        config,
        context: { ...context, companyId, companyName },
        trackingCorrelationId,
      });

      hubble.trackEvent('company_details_displayed', {
        companyId,
        serviceToken,
        currentPageSection: 'salaries',
        trackingCorrelationId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companyName, authenticationStatus]);

  if (model.shouldShowErrorPage) {
    return <ErrorBoundaryContent config={config} />;
  }

  if (!salaryRanges?.salaryRangeByRoles?.length) {
    return <SalariesEmptyState companyName={companyName} />;
  }

  // TODO: Phase 1 only shows salary range by company, to be cleanup at phase 2
  const salaryRangeByRoles = salaryRanges?.salaryRangeByRoles.filter((item) =>
    Boolean(item.rangeByCompany),
  );

  // TODO: Show empty state even there is industry data in phase 1, to be cleanup at phase 2
  if (!salaryRangeByRoles?.length) {
    return <SalariesEmptyState companyName={companyName} />;
  }

  const sortedSalaryRangeByRoles = sortSalaryRangeByRoles({
    salaryRangeByRoles: salaryRangeByRoles || [],
    language: config.language,
  });

  return (
    <>
      <HiddenVisually>
        <Heading level="1">{t('Company salaries', { companyName })}</Heading>
      </HiddenVisually>
      <Box paddingTop="large">
        <Stack space={'large'}>
          {sortedSalaryRangeByRoles ? (
            <SalariesListingSection
              salaryRangeByRoles={sortedSalaryRangeByRoles}
              companyName={companyName}
            />
          ) : null}
          {!!model.salarySatisfactionRating && (
            <SalarySatisfactionSection model={model} />
          )}
          {model.jobsModel.jobs.length > 0 && (
            <OpenJobsSection
              model={model}
              getPaginatedJobs={getPaginatedJobs}
              trackJobsPaginationClicked={trackJobsPaginationClicked}
            />
          )}
          {model.companyReviewsAISummary && (
            <CompanyReviewAISummarySection
              model={model}
              onActiveTabChanged={onActiveTabChanged}
              trackReviewsClicked={trackReviewsClicked}
            />
          )}
          {model.randomCompaniesInThisIndustry &&
            model.randomCompaniesInThisIndustry.length !== 0 && (
              <CompaniesList
                title={t('Other companies in this industry')}
                allCompanies={model.randomCompaniesInThisIndustry}
                trackCompanyProfileLinkClicked={
                  trackRandomCompanyProfileLinkClicked
                }
                isHiddenBelowTablet={false}
              />
            )}
        </Stack>
      </Box>
    </>
  );
};
